import {
  selectCurrentGroupId,
  selectCurrentGroupName,
  selectHotelList,
  selectPathName
} from 'accessors'
import { postAndSelectSubsidiary, savePrevPath } from 'actions'
import { PrimaryBigButton } from 'components/ReusableComponents/Button/BigButtons'
import Checkbox from 'components/ReusableComponents/Checkbox'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import PmsLoginResultDialog from 'features/PMSLogin/TryLoginDialog'
import { PMSnames, PMSs } from 'main/hotelBrandsAndChains'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import pmsApi from 'services/api/pms'
import { saveSubsidiary } from 'utils/helpers'

import PMSLogo from '../helpers/PMSLogo'
import styles from '../PMSLogin.module.scss'
import { useGXPLogin } from './GXPLoginContext'

export function getGroupHotels(hotelList, currentGroupId, pms = PMSs.marriottgxp) {
  if (currentGroupId == null) return []

  return [...(hotelList || [])]
    .filter(
      hotel =>
        String(hotel.merchantId) === String(currentGroupId) &&
        (hotel.pms || '').toLowerCase() === pms.toLowerCase()
    )
    .sort((firstHotel, secondHotel) =>
      (firstHotel.subsidiaryName || '').localeCompare(secondHotel.subsidiaryName || '')
    )
}

export function getHotelListItems(groupHotels, externalHotelIds) {
  if (groupHotels.length > 0) {
    return groupHotels.map(hotel => ({
      id: hotel.subsidiaryId,
      subsidiaryId: hotel.subsidiaryId,
      displayId: hotel.displayId != null ? `#${hotel.displayId}` : '-',
      name:
        hotel.subsidiaryName ||
        (hotel.displayId ? `Hotel ${hotel.displayId}` : 'Unnamed hotel')
    }))
  }

  return (externalHotelIds || []).map(externalHotelId => ({
    id: externalHotelId,
    subsidiaryId: null,
    displayId: externalHotelId,
    name: externalHotelId
  }))
}

export function getLoginSuccessResult() {
  return { isTryLoginSuccess: true, debugInfo: null }
}

export function getLoginFailureResult(error) {
  const message = error?.message || `${error}`

  return { isTryLoginSuccess: false, debugInfo: JSON.stringify({ Message: message }) }
}

export default function GXPCredentialsForm() {
  const dispatch = useDispatch()
  const currentGroupId = useSelector(selectCurrentGroupId)
  const groupName = useSelector(selectCurrentGroupName)
  const hotelList = useSelector(selectHotelList)
  const pathName = useSelector(selectPathName)
  const { data } = useGXPLogin()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [gmCredentialCheck, setGmCredentialCheck] = useState(false)
  const [showHotelListDialog, setShowHotelListDialog] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loginResult, setLoginResult] = useState(null)

  const hotelGroupLabel = groupName?.trim() || 'this hotel group'
  const groupHotels = getGroupHotels(hotelList, currentGroupId)
  const hotelListItems = getHotelListItems(groupHotels, data?.externalHotelIds)
  const hotelCount = hotelListItems.length

  const isSaveDisabled = () =>
    !username?.trim() || !password || !gmCredentialCheck || isSubmitting

  function onLoginClick(e) {
    e.preventDefault()
    if (isSaveDisabled()) return
    setShowConfirmDialog(true)
  }

  function closeConfirmDialog() {
    setShowConfirmDialog(false)
  }

  function goToHotel(subsidiaryId) {
    setShowHotelListDialog(false)
    dispatch(savePrevPath(pathName))
    saveSubsidiary({ subsidiaryId })
    dispatch(postAndSelectSubsidiary(subsidiaryId))
  }

  function confirmApplyCredentials() {
    setShowConfirmDialog(false)
    setIsSubmitting(true)
    pmsApi
      .updateMarriottGxpCredentials({
        username: username.trim(),
        password
      })
      .then(() => {
        setLoginResult(getLoginSuccessResult())
      })
      .catch(error => {
        setLoginResult(getLoginFailureResult(error))
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  function closeLoginResult() {
    setLoginResult(null)
    setPassword('')
  }

  if (isSubmitting || loginResult) {
    return (
      <PmsLoginResultDialog
        data={loginResult}
        isTryingLogin={isSubmitting}
        brand={PMSnames.marriottgxp}
        shortName={hotelGroupLabel}
        successMessage={`Login successful. These credentials now apply to all hotels under ${hotelGroupLabel}.`}
        onClose={closeLoginResult}
      />
    )
  }

  return (
    <>
      <div className={styles.wrapper}>
        <CustomHeader title="PMS Sync" width={720} />
        <div className={styles.card}>
          <div className={styles.cardDesc}>
            <div className={styles.logo}>
              <PMSLogo pms={PMSs.marriottgxp} />
            </div>
            <div className={styles.cardForm}>
              <p className={styles.title}>Marriott GXP login</p>
              <p className={styles.faded}>
                These credentials apply to the entire hotel group{' '}
                <span className={styles.bold}>{hotelGroupLabel}</span>
                {hotelCount > 0 ? ' (' : ''}
                {hotelCount > 0 ? (
                  <button
                    type="button"
                    className={styles.hotelCountButton}
                    onClick={() => setShowHotelListDialog(true)}
                  >
                    {hotelCount} {PMSnames.marriottgxp} hotels
                  </button>
                ) : null}
                {hotelCount > 0 ? ')' : ''}, not only the selected property.
              </p>
              <form onSubmit={onLoginClick} className={styles.form}>
                <TextInput
                  label="Username"
                  required
                  value={username}
                  onChangeValue={setUsername}
                  autoComplete="off"
                  className={styles.input}
                />
                <TextInput
                  label="Password"
                  required
                  type="password"
                  value={password}
                  onChangeValue={setPassword}
                  autoComplete="off"
                  className={styles.input}
                />
                <label className={styles.checkboxLabel}>
                  <Checkbox
                    onChange={e => setGmCredentialCheck(e.target.checked)}
                    checked={gmCredentialCheck}
                  />
                  <span className={styles.checkboxLabelText}>
                    I didn't use GM credentials
                  </span>
                </label>
                <PrimaryBigButton
                  className={styles.primaryButton}
                  disabled={isSaveDisabled()}
                >
                  Login
                </PrimaryBigButton>
                <NavLink to="/">
                  <button type="button" className={styles.secondaryButton}>
                    Skip for now
                  </button>
                </NavLink>
              </form>
            </div>
          </div>
        </div>
      </div>

      {showHotelListDialog ? (
        <ConfirmationDialog
          title={`${PMSnames.marriottgxp} hotels in ${hotelGroupLabel}`}
          positiveMessage="Close"
          onConfirm={() => setShowHotelListDialog(false)}
          onClose={() => setShowHotelListDialog(false)}
          maxWidth={520}
        >
          <div className={styles.hotelList}>
            <table className={styles.hotelTable}>
              <thead>
                <tr>
                  <th className={styles.hotelTableIdColumn}>ID</th>
                  <th>Hotel</th>
                  <th className={styles.hotelTableActionColumn}></th>
                </tr>
              </thead>
              <tbody>
                {hotelListItems.map(hotel => (
                  <tr key={hotel.id}>
                    <td className={styles.hotelTableIdColumn}>{hotel.displayId}</td>
                    <td>{hotel.name}</td>
                    <td className={styles.hotelTableActionColumn}>
                      {hotel.subsidiaryId ? (
                        <button
                          type="button"
                          className={styles.goToHotelButton}
                          onClick={() => goToHotel(hotel.subsidiaryId)}
                        >
                          Go to hotel
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ConfirmationDialog>
      ) : null}

      {showConfirmDialog ? (
        <ConfirmationDialog
          title="Apply credentials to hotel group"
          positiveMessage="Yes, apply to all hotels"
          negativeMessage="Cancel"
          onConfirm={confirmApplyCredentials}
          onReject={closeConfirmDialog}
          onClose={closeConfirmDialog}
        >
          <p style={{ fontSize: 14, lineHeight: '20px' }}>
            These GXP login credentials will be applied to <strong>all hotels</strong>{' '}
            under <strong>{hotelGroupLabel}</strong>
            {hotelCount > 0 ? ` (${hotelCount} hotels)` : ''}. Continue?
          </p>
        </ConfirmationDialog>
      ) : null}
    </>
  )
}
